///////////////////////////////////////////////////////////////////////////////
// Market landing pages (/uk, later /us ...): one config per market, one page
// component (MarketHome.jsx). A market lists WHAT to feature; names, logos and
// prices always come from the live catalogue, so a brand we stop selling simply
// drops off the page.
//
// Prices are quoted in the market's currency. Orders are still charged in USD
// or THB at checkout (lib/checkoutCurrency.js), and the checkout shows the
// exact total before the customer pays.
///////////////////////////////////////////////////////////////////////////////

export const MARKETS = {
  uk: {
    key: 'uk',
    iso: 'GB',
    slug: 'united-kingdom',          // /topup/united-kingdom, /gifts/united-kingdom
    name: 'United Kingdom',
    place: 'the United Kingdom',
    voiceExample: '“£20 Apple gift card” or “giffgaff top up”',
    flag: '🇬🇧',
    currency: 'GBP',
    symbol: '£',
    title: 'Gift Cards, Gaming Cards, Mobile Top-ups & eSIM Data in the UK | MobileTopup.com',
    description: 'Gift cards, gaming cards, UK mobile top-ups and eSIM data in one place. Apple, Google Play, Amazon, EE, giffgaff, O2 and more. Delivered in seconds, priced in £.',
    headline: 'MobileTopup is the UK’s best online store for gift cards, gaming cards, mobile top-ups & eSIM data',
    // Zendit provider names, in display order.
    gifts: ['Apple', 'Google Play', 'Amazon', 'Uber', 'Just Eat', 'Deliveroo', 'Starbucks'],
    games: ['Xbox', 'Playstation Store', 'Nintendo', 'Roblox', 'Google Play', 'Apple'],
    // Full-size carrier tiles, by brand slug; every other UK network gets a small box.
    networks: ['ee', 'o2', 'vodafone', 'three', 'giffgaff', 'tesco-mobile'],
    // Mixed "Most popular" row: kind:key
    popular: ['gift:Apple', 'topup:ee', 'topup:giffgaff', 'gift:Google Play', 'gift:Amazon', 'esim:EU'],
    // eSIM destinations a UK customer buys for a trip, by ISO.
    esim: ['ES', 'US', 'FR', 'IT', 'TR'],
    // Plate colours where the logo needs a specific backing (the manifest's
    // own brand colour is used for networks otherwise).
    plates: {
      apple: '#f3f3f5', 'google play': '#ffffff', amazon: '#ffffff', uber: '#000000', 'uber eats': '#f3f3f5',
      'just eat': '#ff8000', deliveroo: '#00ccbc', starbucks: '#ffffff', xbox: '#ffffff',
      'playstation store': '#f3f3f5', nintendo: '#fa1116', roblox: '#000000',
      ee: '#00b6b0', lyca: '#00d878', three: '#2a2a2e', giffgaff: '#f3f3f5', vodafone: '#ffffff',
    },
    // Display names where the supplier's spelling isn't the brand's.
    names: { giffgaff: 'giffgaff' },
  },
  us: {
    key: 'us',
    iso: 'US',
    slug: 'united-states',
    name: 'United States',
    place: 'the United States',
    voiceExample: '“$25 Apple gift card” or “AT&T top up”',
    flag: '🇺🇸',
    currency: 'USD',
    symbol: '$',
    title: 'Gift Cards, Gaming Cards, Mobile Top-ups & eSIM Data in the US | MobileTopup.com',
    description: 'Gift cards, gaming cards, US prepaid top-ups and eSIM data in one place. Apple, Google Play, Netflix, AT&T, T-Mobile, Verizon and more. Delivered in seconds.',
    headline: 'MobileTopup is America’s best online store for gift cards, gaming cards, mobile top-ups & eSIM data',
    // Not every UK favourite exists here: Amazon, Spotify, Steam and Walmart are not in the US catalogue.
    gifts: ['Apple', 'Google Play', 'Netflix', 'Uber', 'Uber Eats', 'DoorDash'],
    games: ['Xbox', 'Playstation Store', 'Nintendo', 'Roblox', 'Razer Gold', 'Apple'],
    networks: ['at-t', 't-mobile', 'verizon-wireless', 'boost-mobile', 'cricket-mobile', 'metro-pcs'],
    // "Verizon" is a separate, odd-amount product next to "Verizon Wireless"; one tile is enough.
    hide: ['verizon'],
    popular: ['gift:Apple', 'topup:at-t', 'topup:t-mobile', 'gift:Google Play', 'topup:verizon-wireless', 'esim:MX'],
    esim: ['MX', 'CA', 'GB', 'JP', 'FR'],
    plates: {
      apple: '#f3f3f5', 'google play': '#ffffff', netflix: '#000000', uber: '#000000', 'uber eats': '#f3f3f5',
      doordash: '#eb1700', xbox: '#ffffff', 'playstation store': '#f3f3f5', nintendo: '#fa1116', roblox: '#000000',
      'razer gold': '#000000', 't-mobile': '#f3f3f5',
    },
    names: {},
  },
};

// The market home for a country the visitor chose ('GB' -> '/uk'), or null.
export function marketPathForIso(iso) {
  const code = String(iso || '').toUpperCase();
  const m = Object.values(MARKETS).find((x) => x.iso === code);
  return m ? `/${m.key}` : null;
}

export function marketForPath(path) {
  const m = /^\/([a-z]{2})\/?$/.exec(String(path || ''));
  return m ? MARKETS[m[1]] || null : null;
}
